import { useMemo, useCallback, memo } from 'react'
import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'
import { getAvailableModels, saveSelectedModel, AUTO_MODEL_VALUE } from '@/utils/modelSelection'
import { Dropdown, type DropdownOption } from '@/components/ui/Dropdown'
import { useModels } from '@/hooks/useModels'

export const ModelSelector = memo(() => {
  const runtime = useAgentRuntime()
  const composer = useAgentStore((state) => state.composer)
  const contextKey = useAgentStore((state) => state.contextKey)
  const setComposerOptions = useAgentStore((state) => state.setComposerOptions)

  const configModels = runtime.config.models
  const { data: fetchedModels, isLoading: isLoadingModels } = useModels()

  // Memoize available models filtered by context
  const availableModels = useMemo(() => {
    const modelsToUse = fetchedModels && fetchedModels.length > 0 ? fetchedModels : configModels
    const allModels = getAvailableModels(modelsToUse, runtime.config.transport)
    return allModels.filter((model) => !model.contexts?.length || model.contexts.includes(contextKey))
  }, [fetchedModels, configModels, runtime.config.transport, contextKey])

  // Memoize dropdown options with sorted models (Auto first, then alphabetically)
  const dropdownOptions = useMemo<DropdownOption[]>(() => {
    const modelOptions: DropdownOption[] = availableModels
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      .map((model) => ({ value: model.id, label: model.label }))

    return [{ value: AUTO_MODEL_VALUE, label: 'Auto' }, ...modelOptions]
  }, [availableModels])

  // Memoize selected model value
  const selectedModel = useMemo(
    () => (composer.model !== undefined ? composer.model : AUTO_MODEL_VALUE),
    [composer.model]
  )

  // Memoize change handler
  const handleModelChange = useCallback(
    (modelId: string) => {
      if (modelId === AUTO_MODEL_VALUE) {
        setComposerOptions({ model: undefined })
        saveSelectedModel(runtime.config.transport, AUTO_MODEL_VALUE)
      } else {
        setComposerOptions({ model: modelId })
        saveSelectedModel(runtime.config.transport, modelId)
      }
    },
    [setComposerOptions, runtime.config.transport]
  )

  // Early return for loading state
  if (isLoadingModels && !configModels?.length) {
    return null
  }

  // Early return if no options (should never happen since "Auto" is always included)
  if (dropdownOptions.length === 0) {
    return null
  }

  return (
    <Dropdown
      options={dropdownOptions}
      value={selectedModel}
      onChange={handleModelChange}
      placeholder='Select model...'
      searchable
      searchPlaceholder='Search models...'
      placement='top-start'
      triggerClassName='mp-agent-model-selector-trigger'
    />
  )
})

export default ModelSelector
